import {
  FlagOutlined,
  LockOutlined,
  MoneyCollectOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
  SolutionOutlined,
  SettingOutlined,
  UserSwitchOutlined,
  UsergroupAddOutlined,
  ShareAltOutlined,
  AppstoreFilled,
  GroupOutlined,
  GlobalOutlined,
  SnippetsOutlined,
} from "@ant-design/icons";
import {
  Permission,
  Resource,
  UserType,
} from "components/CurrentUser/constants";
import BlankLayout from "layouts/BlankLayout";
import React from "react";
import { Redirect } from "react-router";
import { IRouteConfig } from "services/typings";
import { lazy } from "utils/route";

const routes: IRouteConfig[] = [
  {
    path: "/account",
    name: "account.title",
    icon: UserOutlined,
    component: BlankLayout,
    routes: [
      {
        path: "/account/profile",
        name: "account.my_profile.title",
        icon: UserOutlined,
        component: lazy(() => import("./pages/MyProfile")),
        searchable: true,
        routes: [
          {
            path: "/account/profile",
            component: () => <Redirect to="/account/profile/information" />,
            hideInMenu: true,
            exact: true,
          },
          {
            path: "/account/profile/information",
            name: "account.my_profile.information.title",
            icon: UserOutlined,
            hideInMenu: true,
            component: lazy(
              () => import("./pages/MyProfile/pages/Information")
            ),
            exact: true,
            searchable: true,
          },
          {
            path: "/account/profile/billing",
            name: "account.my_profile.billing.title",
            icon: MoneyCollectOutlined,
            hideInMenu: true,
            component: lazy(() => import("./pages/MyProfile/pages/Billing")),
            exact: true,
            searchable: true,
          },
          {
            path: "/account/profile/password",
            name: "account.my_profile.password.title",
            hideInMenu: true,
            icon: LockOutlined,
            component: lazy(() => import("./pages/MyProfile/pages/Password")),
            exact: true,
            searchable: true,
          },
        ],
      },
      {
        path: "/account/config",
        component: lazy(() => import("./pages/Configuration")),
        icon: SettingOutlined,
        name: "account.configuration.title",
        routes: [
          {
            path: "/account/config",
            component: () => <Redirect to="/account/config/campaign" />,
            hideInMenu: true,
            exact: true,
          },
          {
            path: "/account/config/campaign",
            name: "account.configuration.campaign",
            hideInMenu: true,
            component: lazy(
              () => import("./pages/Configuration/pages/Campaign")
            ),
            exact: true,
          },
          {
            path: "/account/config/token",
            name: "account.configuration.token",
            permissionUser: [UserType.KOL, UserType.PARTNER],
            hideInMenu: true,
            component: lazy(() => import("./pages/Configuration/pages/Token")),
            exact: true,
          },
          {
            path: "/account/config/website",
            name: "account.configuration.website.title",
            permissions: [
              [Resource.WEBSITE_CONFIGURATION, Permission.VIEW],
              [Resource.WEBSITE_CONFIGURATION, Permission.FULL_VIEW],
              [Resource.WEBSITE_CONFIGURATION, Permission.FULL_CREATE],
              [Resource.WEBSITE_CONFIGURATION, Permission.CREATE],
            ],
            hideInMenu: true,
            component: lazy(
              () => import("./pages/Configuration/pages/Website")
            ),
            exact: true,
          },
        ],
      },
      {
        path: "/account/sub-user",
        component: BlankLayout,
        icon: UsergroupAddOutlined,
        name: "account.sub_user.title",
        permissions: [
          [Resource.SUB_USER, Permission.VIEW],
          [Resource.SUB_USER, Permission.FULL_VIEW],
        ],
        routes: [
          {
            path: "/account/sub-user",
            component: lazy(() => import("./pages/SubUser")),
            hideInMenu: true,
            name: "account.sub_user.title",
            exact: true,
            permissions: [
              [Resource.SUB_USER, Permission.VIEW],
              [Resource.SUB_USER, Permission.FULL_VIEW],
            ],
          },
          {
            path: "/account/sub-user/create",
            component: lazy(
              () => import("./pages/SubUser/pages/SubUserCompose")
            ),
            name: "account.sub_user.create",
            hideInMenu: true,
            exact: true,
            permissions: [
              [Resource.SUB_USER, Permission.CREATE],
              [Resource.SUB_USER, Permission.FULL_CREATE],
            ],
          },
        ],
      },
      {
        path: "/account/share-access",
        name: "account.share_access.title",
        icon: AppstoreFilled,
        component: lazy(
          () => import("./pages/ShareAccess/pages/ShareAccessList")
        ),
        exact: true,
        permissions: [[Resource.SHARE_ACCESS, Permission.VIEW]],
      },
      {
        path: "/account/share-access/create",
        name: "account.share_access.create",
        component: lazy(
          () => import("./pages/ShareAccess/pages/ShareAccessCompose")
        ),
        exact: true,
        hideInMenu: true,
        permissions: [
          [Resource.SHARE_ACCESS, Permission.CREATE],
          [Resource.SHARE_ACCESS, Permission.FULL_CREATE],
        ],
      },
      {
        path: "/account/contract",
        name: "account.contract.title",
        icon: SolutionOutlined,
        // component: lazy(() => import("./pages/Contract")),
        component: BlankLayout,
        searchable: true,
        permissions: [[Resource.USER_CONTRACT, Permission.VIEW]],
        suspense: false,
        linkTo: "/account/contract",
        routes: [
          {
            path: "/account/contract",
            name: "account.contract.title",
            icon: SolutionOutlined,
            component: lazy(() => import("./pages/Contract")),
            searchable: true,
            permissions: [[Resource.USER_CONTRACT, Permission.VIEW]],
            exact: true,
            hideInMenu: true,
          },
          {
            path: "/account/contract/appendix",
            name: "account.contract.appendix",
            icon: SnippetsOutlined,
            component: lazy(() => import("./pages/Contract/ContractAppendix")),
            searchable: true,
            exact: true,
            permissions: [[Resource.USER_CONTRACT, Permission.VIEW]],
          },
          {
            path: "/account/contract/appendix/:id",
            name: "account.contract.title",
            icon: SolutionOutlined,
            component: lazy(
              () => import("./pages/Contract/ContractAppendixDetail")
            ),
            searchable: true,
            hideInMenu: true,
            permissions: [[Resource.USER_CONTRACT, Permission.VIEW]],
          },
        ],
      },
      {
        path: "/account/contract-advertiser",
        name: "account.advertiser_contract.title",
        icon: SolutionOutlined,
        component: lazy(() => import("./pages/ContractAdvertiser")),
        searchable: true,
        permissions: [[Resource.USER_ADVERTISER_CONTRACT, Permission.VIEW]],
      },
      {
        path: "/account/user-management",
        name: "account.user_management.title",
        icon: UnorderedListOutlined,
        component: BlankLayout,
        permissions: [
          [Resource.USER, Permission.VIEW],
          [Resource.USER, Permission.FULL_VIEW],
          [Resource.ROLE, Permission.VIEW],
          [Resource.ROLE, Permission.FULL_VIEW],
          [Resource.PENDING_USER, Permission.FULL_VIEW],
          [Resource.GROUP, Permission.VIEW],
          [Resource.GROUP, Permission.FULL_VIEW],
        ],
        routes: [
          {
            path: "/account/user-management/approve-user",
            name: "account.user_management.approve_user.title",
            icon: UserSwitchOutlined,
            component: lazy(
              () => import("./pages/UserManagement/pages/ApproveUser")
            ),
            permissions: [[Resource.PENDING_USER, Permission.FULL_VIEW]],
            exact: true,
            searchable: true,
            placeholderConfig: {
              filter: true,
            },
          },
          {
            path: "/account/user-management/approve-user/:id",
            name: "account.user_management.user.title",
            hideInMenu: true,
            component: lazy(
              () =>
                import(
                  "./pages/UserManagement/pages/ApproveUser/ApproveUserDetail"
                )
            ),
            permissions: [[Resource.PENDING_USER, Permission.FULL_VIEW]],
            exact: true,
          },
          {
            path: "/account/user-management/user",
            name: "account.user_management.user.title",
            icon: TeamOutlined,
            component: lazy(() => import("./pages/UserManagement/pages/User")),
            permissions: [
              [Resource.USER, Permission.VIEW],
              [Resource.USER, Permission.FULL_VIEW],
            ],
            exact: true,
            searchable: true,
            placeholderConfig: {
              filter: true,
            },
          },
          {
            path: "/account/user-management/user/create",
            name: "account.user_management.user.new_user",
            hideInMenu: true,
            component: lazy(
              () => import("./pages/UserManagement/pages/UserCompose")
            ),
            permissions: [
              [Resource.USER, Permission.CREATE],
              [Resource.USER, Permission.FULL_CREATE],
            ],
            exact: true,
            searchable: true,
          },
          {
            path: "/account/user-management/user/:id",
            name: "account.user_management.user.title",
            hideInMenu: true,
            component: lazy(
              () => import("./pages/UserManagement/pages/UserCompose")
            ),
            permissions: [
              [Resource.USER, Permission.VIEW],
              [Resource.USER, Permission.FULL_VIEW],
              [Resource.USER, Permission.UPDATE],
              [Resource.USER, Permission.FULL_UPDATE],
            ],
            exact: true,
          },
          {
            path: "/account/user-management/role",
            name: "account.user_management.role.title",
            icon: FlagOutlined,
            component: lazy(() => import("./pages/UserManagement/pages/Role")),
            exact: true,
            permissions: [
              [Resource.ROLE, Permission.VIEW],
              [Resource.ROLE, Permission.FULL_VIEW],
            ],
            searchable: true,
            placeholderConfig: {
              filter: true,
            },
          },
          {
            path: "/account/user-management/role/create",
            name: "account.user_management.role.new_role",
            component: lazy(
              () => import("./pages/UserManagement/pages/RoleCompose")
            ),
            exact: true,
            hideInMenu: true,
            permissions: [
              [Resource.ROLE, Permission.CREATE],
              [Resource.ROLE, Permission.FULL_CREATE],
            ],
            searchable: true,
          },
          {
            path: "/account/user-management/role/:id",
            name: "account.user_management.role.title",
            component: lazy(
              () => import("./pages/UserManagement/pages/RoleCompose")
            ),
            exact: true,
            hideInMenu: true,
            permissions: [
              [Resource.ROLE, Permission.VIEW],
              [Resource.ROLE, Permission.FULL_VIEW],
              [Resource.ROLE, Permission.UPDATE],
              [Resource.ROLE, Permission.FULL_UPDATE],
            ],
          },
          {
            path: "/account/user-management/group",
            name: "account.user_management.group.title",
            icon: GroupOutlined,
            component: lazy(() => import("./pages/UserManagement/pages/Group")),
            exact: true,
            permissions: [
              [Resource.GROUP, Permission.VIEW],
              [Resource.GROUP, Permission.FULL_VIEW],
            ],
            searchable: true,
            placeholderConfig: {
              filter: true,
            },
          },
          {
            path: "/account/user-management/group/create",
            hideInMenu: true,
            name: "account.user_management.group.new_group",
            component: lazy(
              () => import("./pages/UserManagement/pages/GroupCompose")
            ),
            exact: true,
            permissions: [
              [Resource.GROUP, Permission.CREATE],
              [Resource.GROUP, Permission.FULL_CREATE],
            ],
            searchable: true,
          },
          {
            path: "/account/user-management/group/:id",
            hideInMenu: true,
            name: "account.user_management.group.new_group",
            component: lazy(
              () => import("./pages/UserManagement/pages/GroupCompose")
            ),
            exact: true,
            permissions: [
              [Resource.GROUP, Permission.VIEW],
              [Resource.GROUP, Permission.FULL_VIEW],
              [Resource.GROUP, Permission.CREATE],
              [Resource.GROUP, Permission.FULL_CREATE],
            ],
            searchable: true,
          },
          {
            path: "*",
            component: lazy(() => import("components/base/Errors/NotFound")),
            hideInMenu: true,
          },
        ],
      },
      {
        path: "/account/share-access-management",
        name: "account.share_access_management.title",
        icon: ShareAltOutlined,
        component: lazy(() => import("./pages/ShareAccessManagement")),
        searchable: true,
        exact: true,
        permissions: [
          [Resource.SHARE_ACCESS_MANAGEMENT, Permission.FULL_VIEW],
          [Resource.SHARE_ACCESS_MANAGEMENT, Permission.VIEW],
        ],
      },
      {
        path: "/account/share-access-management/create",
        name: "account.share_access_management.create",
        hideInMenu: true,
        exact: true,
        component: lazy(
          () =>
            import(
              "./pages/ShareAccessManagement/pages/ShareAccessManagementCompose"
            )
        ),
        searchable: true,
        permissions: [
          [Resource.SHARE_ACCESS_MANAGEMENT, Permission.CREATE],
          [Resource.SHARE_ACCESS_MANAGEMENT, Permission.FULL_CREATE],
        ],
      },
      {
        path: "/account/website-management",
        name: "account.website-management.title",
        icon: GlobalOutlined,
        component: lazy(() => import("./pages/WebsiteManagement")),
        searchable: true,
        exact: true,
        permissions: [[Resource.WEBSITE_CONFIGURATION, Permission.FULL_VIEW]],
      },
      {
        path: "*",
        component: lazy(() => import("components/base/Errors/NotFound")),
        hideInMenu: true,
      },
    ],
  },
];

export default routes;
